import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { SkipToContent, Title } from '../GlobalHeader/components';
import { JuvareLogoFull } from '../JuvareLogoFull';
import { themeColors } from '../JuvareTheme';
import { useCommonWebTranslation, useSmallDevice } from '../hooks';
import { BackgroundImages, JuvareIconButton } from './components';
import { HeaderMenu } from './components/HeaderMenu';
/**
 * Renders the main structure of a landing page with optional app branding,
 * content area, login form, header navigation, and footer.
 *
 * Responsive layout adjusts for small and large devices. Includes logic for
 * collapsing long content, toggling dialogs, and rendering background/branding.
 *
 * @param {LandingPageProps} props - Component props.
 * @param {string} [props.image] - Optional image URL for the right-side illustration.
 * @param {string} [props.juvareHref] - Optional URL for Juvare logo click.
 * @param {() => void} [props.juvareOnClick] - Optional click handler for the Juvare logo.
 * @param {React.ReactNode} [props.appImage] - Custom image element for branding.
 * @param {React.ReactNode} props.appTitle - App title, typically a branding element.
 * @param {'local' | 'dev' | 'lab' | 'demo'} [props.environment] - Optional environment label.
 * @param {string} [props.environmentLabel] - Optional text label for the environment.
 * @param {string} [props.contentTitle] - Title for the content section.
 * @param {(dialog: boolean) => React.ReactNode} [props.contentElement] - Optional content area render function.
 * @param {string} [props.contentHtml] - Optional raw HTML content for the content section.
 * @param {boolean} [props.border] - Whether to show a border around the content.
 * @param {React.ReactNode} [props.loginElement] - Element to display as a login form.
 * @param {LandingAppPage[]} props.pages - Array of landing pages used for navigation.
 * @param {string} [props.backToLoginLabel] - Optional label for "Back to Login" button.
 * @param {string} [props.basename] - Router basename path.
 * @param {React.ReactNode} [props.footerCopyright] - Optional footer copyright element.
 * @param {LandingPageHeaderItem[]} [props.headerItems] - Optional top header navigation items.
 * @param {() => void} [props.backToLoginOnClick] - Callback when back to login is clicked (used when no loginElement present).
 * @returns {JSX.Element} The rendered landing page.
 */
export const LandingPage = (props) => {
    var _a, _b, _c, _d, _e;
    const { t } = useCommonWebTranslation();
    const smallDevice = useSmallDevice();
    const collapseTopMenu = useMediaQuery((theme) => theme.breakpoints.down('lg'), { noSsr: true });
    const contentRef = React.useRef(undefined);
    const [showMore, setShowMore] = React.useState(false);
    const [showMoreDialog, setShowMoreDialog] = React.useState(false);
    React.useEffect(() => {
        const measure = () => {
            var _a;
            if (contentRef.current && ((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) > 280) {
                setShowMore(true);
            }
            else {
                setShowMore(false);
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => {
            window.removeEventListener('resize', measure);
        };
    }, [props.contentTitle, props.contentElement, props.loginElement]);
    return (React.createElement(Box, { sx: {
            height: '100%',
            overflow: 'auto',
        } },
        React.createElement(Box, { sx: {
                position: 'relative',
                minHeight: '100%',
                backgroundColor: themeColors.surface.primary,
            } },
            React.createElement(SkipToContent, null),
            React.createElement(Box, { component: 'header', sx: { position: 'relative', display: 'flex', gap: 1, pl: 2, pr: 2, pt: 1, pb: 1 } },
                props.appImage != null ? (React.createElement(Box, { sx: { display: 'flex', alignItems: 'center' } }, props.appImage)) : (React.createElement(JuvareIconButton, { href: props.juvareHref, onClick: props.juvareOnClick })),
                smallDevice ? (React.createElement(Box, { sx: { flexGrow: 1 } })) : (React.createElement(Title, { appTitle: props.appTitle, environment: props.environment, environmentLabel: props.environmentLabel })),
                collapseTopMenu && props.headerItems && props.headerItems.length > 0 ? (React.createElement(HeaderMenu, { headerItems: props.headerItems })) : ((_a = props.headerItems) === null || _a === void 0 ? void 0 : _a.map(({ label, href, onClick }) => (React.createElement(Button, { key: label, href: href, onClick: onClick
                        ? (event) => {
                            event.preventDefault();
                            onClick();
                        }
                        : undefined }, label))))),
            React.createElement(Box, { id: 'main-content', tabIndex: -1, component: 'main', sx: { position: 'relative', outline: 'none' } },
                React.createElement(Box, { sx: {
                        boxSizing: 'border-box',
                        width: {
                            xs: '100%',
                            sm: '100%',
                            md: '100%',
                            lg: props.loginElement ? '66.667%' : 'max(1080px, 66.667%)',
                            xl: props.loginElement ? '50%' : 'max(1080px, 50%)',
                        },
                        pt: {
                            xs: 2,
                            sm: 4,
                            md: 6,
                            lg: 8,
                            xl: 10,
                        },
                        pl: {
                            xs: 2,
                            sm: 5,
                            md: 14,
                            lg: 14,
                            xl: 14,
                        },
                        pr: {
                            xs: 2,
                            sm: 5,
                            md: 14,
                            lg: 14,
                            xl: 14,
                        },
                        pb: {
                            xs: 14,
                            sm: 14,
                            md: '50vw',
                            lg: 14,
                            xl: 14,
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                    } }, props.loginElement ? (React.createElement(React.Fragment, null,
                    (props.contentElement || props.contentTitle) && (React.createElement(Box, null,
                        React.createElement(Box, { ref: contentRef, sx: {
                                maxHeight: 320,
                                overflow: 'hidden',
                                maskImage: 'linear-gradient(to bottom, black 280px, transparent)',
                            } },
                            props.contentTitle && (React.createElement(Typography, { variant: smallDevice ? 'h1' : 'd3', sx: { mb: 4 } }, props.contentTitle)), (_b = props.contentElement) === null || _b === void 0 ? void 0 :
                            _b.call(props, false)),
                        showMore && (React.createElement(Typography, { sx: { mt: -1, textAlign: 'right' }, variant: 'subtitle2' },
                            React.createElement(Link, { sx: { cursor: 'pointer' }, onClick: () => setShowMoreDialog(true) },
                                t('common.more'),
                                "\u2026"))),
                        React.createElement(Dialog, { open: showMoreDialog, onClose: () => setShowMoreDialog(false) },
                            props.contentTitle && React.createElement(DialogTitle, null, props.contentTitle),
                            React.createElement(DialogContent, null, (_c = props.contentElement) === null || _c === void 0 ? void 0 : _c.call(props, true)),
                            React.createElement(DialogActions, null,
                                React.createElement(Button, { autoFocus: true, onClick: () => setShowMoreDialog(false) }, t('common.ok')))))),
                    React.createElement(Box, { sx: {
                            maxWidth: {
                                xs: undefined,
                                sm: undefined,
                                md: 367,
                                lg: 368,
                                xl: 368,
                            },
                        } }, props.loginElement))) : (React.createElement(Box, null,
                    props.backToLoginOnClick && (React.createElement(Box, { mb: 4 },
                        React.createElement(Button, { startIcon: React.createElement(ArrowBackIcon, null), onClick: () => { var _a; return (_a = props.backToLoginOnClick) === null || _a === void 0 ? void 0 : _a.call(props); } }, (_d = props.backToLoginLabel) !== null && _d !== void 0 ? _d : t('landingPage.backToLogIn')))),
                    props.contentTitle && (React.createElement(Typography, { variant: 'd3', sx: { mb: 4 } }, props.contentTitle)), (_e = props.contentElement) === null || _e === void 0 ? void 0 :
                    _e.call(props, false))))),
            React.createElement(BackgroundImages, { image: props.image }),
            React.createElement(Box, { component: 'footer', sx: {
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    pt: 1,
                    pb: 1,
                    pl: 2,
                    pr: 2,
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                } },
                React.createElement(JuvareLogoFull, null),
                React.createElement(Typography, { variant: 'caption', sx: { pt: '4px', color: themeColors.text.subtle } }, props.footerCopyright)))));
};
